import React from 'react';

import classNames from 'classnames';

import {hasHtmlTags, isEmptyHtml} from './helpers';

import styles from './rich-text-display.module.css';

type Props = {
    html: string;
    size?: 'normal' | 'small' | 'mini';
    centerContent?: boolean;
    placeholderText?: string;
    smallLineHeight?: boolean; // Matches line height of textfields,
    maintainLineBreaks?: boolean;
    color?: 'dark' | 'medium' | 'light';
    shouldTruncate?: boolean;
    truncateInline?: boolean;
    maxLines?: number;
    tooltipText?: string;
    shouldBreakWords?: boolean;
    widthFitContent?: boolean;
};

export function RichTextDisplay(props: Props) {
    const {color = 'dark'} = props;

    const showPlaceholderText = isEmptyHtml(props.html);

    const getDisplayContent = React.useCallback(() => {
        if (showPlaceholderText) {
            return props.placeholderText;
        }

        // If maintainLineBreaks is enabled and we have plain text (no HTML tags)
        if (props.maintainLineBreaks && !hasHtmlTags(props.html)) {
            return props.html.replace(/\n/g, '<br>');
        }

        return props.html;
    }, [props.html, props.placeholderText, props.maintainLineBreaks, showPlaceholderText]);

    const styleName = classNames(styles['rich-text-container'], {
        [styles['center-text']]: props.centerContent,
        [styles['normal-font']]: !props.size || props.size === 'normal',
        [styles['small-font']]: props.size === 'small',
        [styles['mini-font']]: props.size === 'mini',
        [styles['placeholder-text']]: showPlaceholderText,
        [styles['small-line-height']]: props.smallLineHeight,
        [styles['medium-color']]: color === 'medium' && !showPlaceholderText,
        [styles['light-color']]: color === 'light' && !showPlaceholderText,
        [styles.truncate]: props.shouldTruncate,
        [styles['truncate-inline']]: props.truncateInline,
        [styles['max-lines']]: props.maxLines,
        [styles['break-words']]: props.shouldBreakWords,
        [styles['width-fit-content']]: props.widthFitContent,
    });

    const lineHeight = props.smallLineHeight ? 16 : 21;

    return (
        <div
            onClick={(e) => {
                // Prevent propagation of link clicks
                // @ts-expect-error
                if (e.target && e.target.tagName === 'A') {
                    e.stopPropagation();
                }
            }}
            style={props.maxLines ? {maxHeight: `${props.maxLines * lineHeight}px`} : undefined}
            dangerouslySetInnerHTML={{
                // @ts-expect-error
                __html: getDisplayContent(),
            }}
            data-tip={props.tooltipText}
            className={styleName}
        />
    );
}
